import React from "react";
import { Input as AInput, Form } from "antd"; // Ant Design Input and Form components
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons"; // Icons for password visibility toggle
import Flex from "./Flex"; // Custom Flex layout component
import { TYPOGRAPHY } from "../../constants"; // Typography constants
import Typography from "./Typographys";
import { useThemeContext } from "../../theme/GlobalTheme";

/**
 * InputField Component
 *
 * A general input field wrapper around the Ant Design Input component.
 *
 * Props:
 * @param {Object} inputProps - Properties passed to the Ant Design Input.
 * @param {Object} formItemProps - Properties passed to the Ant Design Form.Item (default: {}).
 * Features:
 * - Supports optional labels and required field indicators.
 */
function InputField({ inputProps, formItemProps = {}, value }) {
  const { label } = inputProps;
  const { required } = formItemProps;

  const { isDark } = useThemeContext();
  return (
    <Form.Item {...formItemProps} required={false} className="!mb-3">
      <Flex gap={4} vertical>
        {/* Render label and required indicator if applicable */}
        <Flex gap={4}>
          {label && (
            <Typography text={label} className="!mb-2" type={TYPOGRAPHY.PARA} />
          )}
          {required && (
            <Typography
              className="!pt-1 !text-[#FF0000]"
              text="*"
              type="para"
            />
          )}
        </Flex>
        {/* Ant Design Input component with additional props */}
        <AInput size="large" className="!py-2" value={value} {...inputProps} />
      </Flex>
    </Form.Item>
  );
}

/**
 * InputPassword Component
 *
 * A wrapper for password input fields using Ant Design Input.Password component.
 *
 * Props:
 * @param {Object} inputProps - Properties passed to the Ant Design Input.Password.
 * @param {Object} formItemProps - Properties passed to the Ant Design Form.Item (default: {}).
 * Features:
 * - Toggles password visibility using icons.
 * - Supports labels and required field indicators.
 */
function InputPassword({ inputProps, formItemProps = {} }) {
  const { label } = inputProps;
  const { required } = formItemProps;

  return (
    <Form.Item {...formItemProps} required={false} className="!mb-3">
      <Flex gap={4} vertical>
        {/* Render label and required indicator if applicable */}
        <Flex gap={4}>
          {label && (
            <Typography text={label} className="!mb-2" type={TYPOGRAPHY.PARA} />
          )}
          {required && (
            <Typography
              className="!pt-1 !text-[#FF0000]"
              text="*"
              type="para"
            />
          )}
        </Flex>
        {/* Ant Design Password Input with visibility toggle */}
        <AInput.Password
          className="!py-2 px-3"
          size="large"
          {...inputProps}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
        />
      </Flex>
    </Form.Item>
  );
}

/**
 * Input Component
 *
 * A versatile input component that conditionally renders either an InputField or InputPassword based on type.
 *
 * Props:
 * @param {Object} props - Properties to be passed to the Input or Password component.
 * Features:
 * - Automatically detects and renders the appropriate input type.
 */
function Input(props) {
  if (props?.inputProps?.type === "password") {
    return <InputPassword {...props} />;
  } else {
    return <InputField {...props} />;
  }
}

export default Input;
